import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { motion, useInView, useReducedMotion, type Variants } from 'framer-motion';

export const EASE = [0.22, 1, 0.36, 1] as const;

export const fadeUp: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

export const stagger: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export const Reveal = ({ children, delay = 0, className, y = 24 }: { children: ReactNode; delay?: number; className?: string; y?: number }) => {
    const reduce = useReducedMotion();
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true, margin: '-80px' });
    return (
        <motion.div ref={ref} initial={reduce ? false : { opacity: 0, y }} animate={inView ? { opacity: 1, y: 0 } : undefined} transition={{ duration: 0.7, ease: EASE, delay }} className={className}>
            {children}
        </motion.div>
    );
};

export const useTypewriter = (text: string, speed = 14, start = true) => {
    const [out, setOut] = useState('');
    useEffect(() => {
        if (!start) {
            setOut('');
            return;
        }
        setOut('');
        let i = 0;
        const t = setInterval(() => {
            i++;
            setOut(text.slice(0, i));
            if (i >= text.length) clearInterval(t);
        }, speed);
        return () => clearInterval(t);
    }, [text, speed, start]);
    return out;
};

export const useCountUp = (target: number, inView: boolean, duration = 1400) => {
    const [v, setV] = useState(0);
    useEffect(() => {
        if (!inView) return;
        let raf = 0;
        const start = performance.now();
        const tick = (now: number) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setV(target * eased);
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, inView, duration]);
    return v;
};

export const formatGBP = (n: number) =>
    new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
    }).format(n);

export const useSavingsCalc = (headcount: number, avgSalary: number, turnover: number) =>
    useMemo(() => {
        const exits = Math.round((headcount * turnover) / 100);
        const productivityLoss = (avgSalary / 52) * 12 * 0.6;
        const recruitingCost = avgSalary * 0.2;
        const costPerExit = productivityLoss + recruitingCost;
        const totalAnnualCost = exits * costPerExit;
        const savings = totalAnnualCost * 0.4;
        const rampDaysSaved = Math.round(12 * 7 * 0.4);
        const payback = Math.max(1, Math.round((50000 / savings) * 12));
        return { exits, costPerExit, totalAnnualCost, savings, rampDaysSaved, payback };
    }, [headcount, avgSalary, turnover]);
